import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuickCreate } from "../../next/quickCreate";
import {
  ConfirmDialog,
  FormAlert,
  TableKit,
} from "@NP-Webber/crm-kit";
import { shellApi, type FormDto } from "../../api/shellApi";
import {
  formIntakeApi,
  type FormIntakeDto,
} from "../../api/formIntakeApi";
import { toClientApiError } from "../../api/httpClient";
import { useTableLayoutPrefs } from "../../hooks/useTableLayoutPrefs";
import {
  DeleteOutlinedIcon,
  OpenInNewOutlinedIcon,
} from "../../ui/appIcons";
import { ListPageHeader } from "../../ui/ListPageHeader";
import { ListStatStrip } from "../../ui/ListStatStrip";
import { QuietButton } from "../../ui/QuietButton";
import { TableRowActions } from "../../ui/TableRowActions";
import { FormBuilderDialog } from "./FormBuilderDialog";
import type { FormBuilderSavePayload } from "./formSchema";
import { FormEntityCard } from "./FormEntityCard";
import { FormIntakeCard } from "./FormIntakeCard";
import { FormIntakeWizardDialog } from "./FormIntakeWizardDialog";

export function FormsListPage() {
  const { prefs, savePrefs } = useTableLayoutPrefs("forms.list");
  const [items, setItems] = useState<FormDto[]>([]);
  const [intakes, setIntakes] = useState<FormIntakeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [intakesLoading, setIntakesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [intakeWizardOpen, setIntakeWizardOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [selected, setSelected] = useState<FormDto | null>(null);
  const [intakeCardOpen, setIntakeCardOpen] = useState(false);
  const [selectedIntake, setSelectedIntake] = useState<FormIntakeDto | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [availFilter, setAvailFilter] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  useQuickCreate("form", () => {
    setCreateError(null);
    setBuilderOpen(true);
  });
  // deep link from the global search: /forms?form=<id>
  const formParam = searchParams.get("form");
  useEffect(() => {
    if (!formParam || !items.length) return;
    const hit = items.find((f) => f.id === formParam);
    if (hit) {
      setSelected(hit);
      setCardOpen(true);
    }
  }, [formParam, items]);

  const reloadIntakes = useCallback(async () => {
    setIntakesLoading(true);
    try {
      const next = await formIntakeApi.list();
      setIntakes(next);
      setSelectedIntake((current) => {
        if (!current) return null;
        return next.find((item) => item.id === current.id) ?? null;
      });
    } catch {
      setIntakes([]);
    } finally {
      setIntakesLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await shellApi.listForms();
      setItems(next);
      setSelected((current) => {
        if (!current) return null;
        return next.find((item) => item.id === current.id) ?? null;
      });
    } catch (e) {
      setError(toClientApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    void reloadIntakes();
  }, [reload, reloadIntakes]);

  const openForm = (row: Record<string, unknown>) => {
    const id = String(row.id ?? "");
    if (!id) return true;
    const form = items.find((item) => item.id === id) ?? null;
    if (!form) return true;
    setSelected(form);
    setCardOpen(true);
    return true;
  };

  const openIntake = (row: Record<string, unknown>) => {
    const id = String(row.id ?? "");
    if (!id) return true;
    const intake = intakes.find((item) => item.id === id) ?? null;
    if (!intake) return true;
    setSelectedIntake(intake);
    setIntakeCardOpen(true);
    return true;
  };

  const handleCreate = async (payload: FormBuilderSavePayload) => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await shellApi.createForm(payload);
      setBuilderOpen(false);
      await reload();
      setSelected(created);
      setCardOpen(true);
    } catch (e) {
      setCreateError(toClientApiError(e).message);
    } finally {
      setCreating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 160,
      },
      {
        key: "availableLabel",
        title: "זמין",
        sortable: true,
        filterable: true,
        minWidth: 80,
      },
      {
        key: "publicUrl",
        title: "קישור ציבורי",
        filterable: true,
        minWidth: 220,
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 120,
        render: (_value: unknown, row: Record<string, unknown>) => (
          <TableRowActions>
            <QuietButton
              title="פתח"
              tone="accent"
              icon={<OpenInNewOutlinedIcon />}
              onClick={() => {
                openForm(row);
              }}
            />
            <QuietButton
              title="מחק"
              tone="danger"
              icon={<DeleteOutlinedIcon />}
              onClick={() => setDeleteId(String(row.id ?? ""))}
            />
          </TableRowActions>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items],
  );

  const intakeColumns = useMemo(
    () => [
      {
        key: "name",
        title: "שם",
        sortable: true,
        filterable: true,
        minWidth: 160,
      },
      {
        key: "externalFormId",
        title: "מזהה טופס",
        sortable: true,
        filterable: true,
        minWidth: 120,
      },
      {
        key: "activeLabel",
        title: "פעיל",
        sortable: true,
        filterable: true,
        minWidth: 80,
      },
      {
        key: "webhookUrl",
        title: "Webhook",
        filterable: true,
        minWidth: 220,
      },
      {
        key: "actions",
        title: "פעולות",
        minWidth: 80,
        render: (_value: unknown, row: Record<string, unknown>) => (
          <TableRowActions>
            <QuietButton
              title="פתח"
              tone="accent"
              icon={<OpenInNewOutlinedIcon />}
              onClick={() => {
                openIntake(row);
              }}
            />
          </TableRowActions>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intakes],
  );

  const rows = items.map((f) => ({
    ...f,
    availableLabel: f.isAvailable ? "כן" : "לא",
    publicUrl: `${window.location.origin}/public/forms/${f.publicToken}`,
  }));

  const intakeRows = intakes.map((i) => ({
    ...i,
    activeLabel: i.isActive ? "כן" : "לא",
  }));

  const stats = useMemo(() => {
    const available = items.filter((f) => f.isAvailable).length;
    return {
      total: items.length,
      available,
      unavailable: items.length - available,
    };
  }, [items]);

  const filteredRows = useMemo(() => {
    if (!availFilter) return rows;
    if (availFilter === "available") {
      return rows.filter((r) => r.isAvailable);
    }
    return rows.filter((r) => !r.isAvailable);
  }, [rows, availFilter]);

  return (
    <section className="page-section page-section--list">
      <ListPageHeader
        screen
        onRefresh={() => {
          void reload();
          void reloadIntakes();
        }}
        primaryLabel="טופס חדש"
        onPrimary={() => {
          setCreateError(null);
          setBuilderOpen(true);
        }}
        secondary={
          <QuietButton
            tone="accent"
            onClick={() => setIntakeWizardOpen(true)}
          >
            קליטת טפסים
          </QuietButton>
        }
      />

      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      <ListStatStrip
        title="סיכום טפסים"
        activeKey={availFilter}
        onSelect={setAvailFilter}
        columns={3}
        loading={loading}
        cards={[
          {
            key: null,
            label: "סה״כ טפסים",
            value: stats.total,
            color: "#1976d2",
            iconName: "note",
          },
          {
            key: "available",
            label: "זמינים",
            value: stats.available,
            color: "#2e7d32",
          },
          {
            key: "unavailable",
            label: "לא זמינים",
            value: stats.unavailable,
            color: "#ef6c00",
          },
        ]}
      />

      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={filteredRows as unknown as Array<Record<string, unknown>>}
          loading={loading}
          clientSideMode
          emptyMessage="אין טפסים"
          exportFileName="forms"
          showExport
          showFilters
          showColumnPicker
          showAdvancedSettings
          columnOrder={(prefs.columnOrder as string[] | undefined) ?? undefined}
          onColumnOrderChange={(next: string[]) => {
            void savePrefs({ ...prefs, columnOrder: next });
          }}
          hiddenColumnKeys={(prefs.hiddenColumnKeys as string[] | undefined) ?? undefined}
          onHiddenColumnKeysChange={(next: string[]) => {
            void savePrefs({ ...prefs, hiddenColumnKeys: next });
          }}
          onRowClick={openForm}
          onRowDoubleClick={openForm}
          onOpenCard={openForm}
        />
      </div>

      <h2 className="page-section__subtitle" style={{ marginTop: 24, marginBottom: 8 }}>
        קליטות חיצוניות
      </h2>
      <div className="page-section__table-surface">
        <TableKit
          columns={intakeColumns}
          data={intakeRows as unknown as Array<Record<string, unknown>>}
          loading={intakesLoading}
          clientSideMode
          emptyMessage="אין קליטות טפסים חיצוניות — לחצו «קליטת טפסים»"
          exportFileName="form-intakes"
          showExport
          showFilters
          showColumnPicker
          onRowClick={openIntake}
          onRowDoubleClick={openIntake}
          onOpenCard={openIntake}
        />
      </div>

      <FormEntityCard
        open={cardOpen}
        form={selected}
        onClose={() => {
          setCardOpen(false);
          setSelected(null);
        }}
        onChanged={() => {
          void reload();
        }}
      />

      <FormIntakeCard
        open={intakeCardOpen}
        intake={selectedIntake}
        onClose={() => {
          setIntakeCardOpen(false);
          setSelectedIntake(null);
        }}
        onChanged={() => {
          void reloadIntakes();
        }}
      />

      <FormBuilderDialog
        open={builderOpen}
        value={{}}
        saving={creating}
        error={createError}
        onClose={() => {
          if (!creating) {
            setBuilderOpen(false);
            setCreateError(null);
          }
        }}
        onSave={handleCreate}
      />

      <FormIntakeWizardDialog
        open={intakeWizardOpen}
        onClose={() => {
          setIntakeWizardOpen(false);
          if (selectedIntake) {
            setIntakeCardOpen(true);
          }
        }}
        onSaved={(item) => {
          void reloadIntakes();
          setSelectedIntake(item);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteId)}
        onClose={() => {
          if (!deleting) setDeleteId(null);
        }}
        title="מחיקת טופס"
        message="למחוק את הטופס?"
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={deleting}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => {
          if (!deleteId) return;
          setDeleting(true);
          void shellApi
            .deleteForm(deleteId)
            .then(() => {
              setDeleteId(null);
              void reload();
            })
            .finally(() => setDeleting(false));
        }}
      />
    </section>
  );
}
